import {
    Button,
    ButtonBase,
    Checkbox,
    Chip,
    ClickAwayListener,
    Fade,
    FormControlLabel,
    Grid,
    Paper,
    Popper,
    Radio,
    styled,
    Switch,
} from "@mui/material";
import { each, get } from "lodash";
import { useEffect, useRef, useState } from "react";
import BSInput from "../../../common/js/components/BSInput";
import { DownChevron } from "../../../common/js/icons";
import LocationFilters from "./LocationFilters";
import MarketFilters from "./MarketFilters";
import PropertyTypeFilter from "./PropertyTypeFilter";

const StyledMenuSearchButton = styled(Button)({
    marginBottom: "15px",
});

const StyledPropertyTypeContainer = styled(Paper)({
    width: "32em",
    maxHeight: "78vh",
    overflowY: "scroll",
});

const StyledMarketFilterContainer = styled(Paper)({
    width: "32em",
    maxHeight: "78vh",
    overflowY: "scroll",
});

const StyledStatusContainer = styled(Paper)({
    minWidth: "210px",
    padding: "0 15px",
});

const StyledLocationFilterContainer = styled(Paper)({
    minWidth: "350px",
    padding: "20px",
    maxHeight: "78vh",
    overflowY: "scroll",
});

const StyledSizeLabel = styled(FormControlLabel)({
    marginRight: "0",
    marginLeft: "-14px",
});

const StyledFakeSelect = styled(ButtonBase)({
    height: "39px",
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    marginLeft: 0,
    fontWeight: 400,
    width: "100%",
    justifyContent: "flex-start",
    padding: "0 26px 0 6px",
    backgroundColor: "white",
    fontSize: "14px",
    "&.Mui-focusVisible": {
        border: "1px solid #2C99D2",
        backgroundColor: "white",
    },
});

const StyledFakeSelectIcon = styled("span")({
    position: "absolute",
    right: "6px",
    fontSize: "16px",
});

const StyledFilterBar = styled("div")({
    backgroundColor: "white",
    paddingTop: "15px",
    paddingRight: "20px",
    paddingLeft: "15px",
    borderBottom: "1px solid #eee",
});

const StyledStatusSwitch = styled(Switch)({
    marginRight: "-15px",
});

const StyledMenuTitle = styled("span")({
    fontSize: "14px",
});

const StyledHrDivider = styled("hr")({
    position: "absolute",
    width: "100%",
    left: 0,
    top: "25px",
});

const StyledMenuSectionTitle = styled("h4")({
    color: "#9E9E9E",
    fontSize: "13px",
    fontWeight: 600,
    textTransform: "uppercase",
    marginBottom: "5px",
});

const PropertiesFilters = (props) => {
    const {
        clearFilters,
        handleDeleteCrumb,
        handleSelectAllPropertyType,
        handleSelectMarket,
        handleSelectMicromarket,
        handleSelectSubmarket,
        handleSizeRadioClick,
        loadedSearchCrumbs,
        marketFilterExpansionPanels,
        propertySearchFilters,
        propertyTypeExpansionPanels,
        searchCriteria,
        searchSubmit,
        toggleMarketFilterExpansionPanel,
        togglePropertyTypeExpansionPanel,
        updateFilter,
    } = props;

    const clickedOnDropdownToggleRef = useRef(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [placement, setPlacement] = useState(null);

    useEffect(() => {
        initializeSizeCriteria();
    }, []);

    const initializeSizeCriteria = () => {
        if (
            !get(searchCriteria, "sizeCriteria.availableSf", false) &&
            !get(searchCriteria, "sizeCriteria.totalSf", false) &&
            !get(searchCriteria, "sizeCriteria.acreage", false)
        ) {
            handleSizeRadioClick("availableSf", true);
        }
    };

    const handleClick = (placementArg) => (event, filter) => {
        clickedOnDropdownToggleRef.current = true;
        const { currentTarget } = event;
        if (openDropdown === filter && placement === placementArg) {
            // Toggle off the same dropdown
            setOpenDropdown(null);
            setAnchorEl(null);
        } else {
            // Open new dropdown
            setOpenDropdown(filter);
            setAnchorEl(currentTarget);
            setPlacement(placementArg);
        }
    };

    const handleClose = () => {
        if (clickedOnDropdownToggleRef.current) {
            clickedOnDropdownToggleRef.current = false; // Reset it
            return; // Skip closing — click was on dropdown toggle
        }
        setOpenDropdown(null);
    };

    const onKeyPress = (event) => {
        if (event.key === "Enter") handleSearchSubmit();
    };

    const handleClearFilters = () => {
        clearFilters();
        handleClose();
        handleSizeRadioClick("availableSf", true);
    };

    const handleSearchSubmit = () => {
        clickedOnDropdownToggleRef.current = false;
        searchSubmit();
        handleClose();
    };

    const countPropertyTypeSelected = (searchCriteria) => {
        let count = 0;
        each(get(searchCriteria, "propertyTypes", []), (p) => {
            if (p.selected === true) {
                count += 1;
            }
        });
        return count;
    };

    const countFilter = (searchCriteria, filterName) => {
        let count = 0;
        each(get(searchCriteria, filterName, []), (option) => {
            if (option === true) {
                count += 1;
            }
        });
        return count;
    };

    const combineFilterCount = (searchCriteria, criteriaOne, criteriaTwo) => {
        return countFilter(searchCriteria, criteriaOne) + countFilter(searchCriteria, criteriaTwo);
    };

    return (
        <StyledFilterBar>
            <Grid container alignItems="center" spacing={1}>
                <Grid item x style={{ fontWeight: 400, maxWidth: "70px", marginLeft: "12px", marginRight: "5px" }}>
                    <div>Filters:</div>
                </Grid>
                <Grid item xs sx={{ flexGrow: 1, flexBasis: 0, maxWidth: "100%", minWidth: "Auto" }}>
                    <Popper open={openDropdown === "location"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StyledLocationFilterContainer>
                                            <LocationFilters
                                                updateFilter={updateFilter}
                                                searchCriteria={searchCriteria}
                                                onKeyPress={onKeyPress}
                                                hideFilters={false}
                                            ></LocationFilters>

                                            <Grid container spacing={4} justifyContent="flex-end">
                                                <Grid item>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </StyledLocationFilterContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <StyledFakeSelect onClick={(e) => handleClick("bottom-start")(e, "location")}>
                        {"Location"}{" "}
                        <StyledFakeSelectIcon>
                            <DownChevron />
                        </StyledFakeSelectIcon>
                    </StyledFakeSelect>
                </Grid>
                <Grid item xs sx={{ flexGrow: 1, flexBasis: 0, maxWidth: "100%", minWidth: "Auto" }}>
                    <Popper open={openDropdown === "marketArea"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StyledMarketFilterContainer>
                                            <MarketFilters
                                                searchCriteria={searchCriteria}
                                                toggleMarketFilterExpansionPanel={toggleMarketFilterExpansionPanel}
                                                marketFilterExpansionPanels={marketFilterExpansionPanels}
                                                handleSelectMarket={handleSelectMarket}
                                                handleSelectSubmarket={handleSelectSubmarket}
                                                handleSelectMicromarket={handleSelectMicromarket}
                                            ></MarketFilters>

                                            <Grid
                                                container
                                                spacing={4}
                                                justifyContent="flex-end"
                                                style={{ padding: "10px", paddingRight: "16px" }}
                                            >
                                                <Grid item>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </StyledMarketFilterContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <StyledFakeSelect onClick={(e) => handleClick("bottom-start")(e, "marketArea")}>
                        {`Market Area ${
                            countFilter(searchCriteria, "locationCriteria.markets") > 0
                                ? `(${countFilter(searchCriteria, "locationCriteria.markets")})`
                                : ""
                        }`}
                        <StyledFakeSelectIcon>
                            <DownChevron />
                        </StyledFakeSelectIcon>
                    </StyledFakeSelect>
                </Grid>
                <Grid item xs sx={{ flexGrow: 1, flexBasis: 0, maxWidth: "100%", minWidth: "Auto" }}>
                    <Popper open={openDropdown === "propertyType"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StyledPropertyTypeContainer>
                                            <PropertyTypeFilter
                                                updateFilter={updateFilter}
                                                propertySearchFilters={propertySearchFilters}
                                                handleSelectAllPropertyType={handleSelectAllPropertyType}
                                                searchCriteria={searchCriteria}
                                                handleSearchSubmit={handleSearchSubmit}
                                                togglePropertyTypeExpansionPanel={togglePropertyTypeExpansionPanel}
                                                propertyTypeExpansionPanels={propertyTypeExpansionPanels}
                                            />
                                        </StyledPropertyTypeContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <StyledFakeSelect onClick={(e) => handleClick("bottom-start")(e, "propertyType")}>
                        {`Property Type ${
                            countPropertyTypeSelected(searchCriteria) > 0
                                ? `(${countPropertyTypeSelected(searchCriteria)})`
                                : ""
                        }`}
                        <StyledFakeSelectIcon>
                            <DownChevron />
                        </StyledFakeSelectIcon>
                    </StyledFakeSelect>
                </Grid>
                <Grid item xs sx={{ flexGrow: 1, flexBasis: 0, maxWidth: "100%", minWidth: "Auto" }}>
                    <Popper open={openDropdown === "status"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StyledStatusContainer>
                                            <Grid container justifyContent="space-between" alignItems="center">
                                                <Grid item>
                                                    <StyledMenuTitle>KM Managed Only</StyledMenuTitle>
                                                    <StyledHrDivider />
                                                </Grid>
                                                <Grid item>
                                                    <StyledStatusSwitch
                                                        checked={get(
                                                            searchCriteria,
                                                            "statusCriteria.kmManagedOnly",
                                                            false
                                                        )}
                                                        onChange={(e) =>
                                                            updateFilter(
                                                                "statusCriteria.kmManagedOnly",
                                                                e.target.checked
                                                            )
                                                        }
                                                        value="kmManagedOnly"
                                                        color="primary"
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                item
                                                xs={12}
                                                sx={{ flexGrow: 0, flexBasis: "100%", maxWidth: "100%" }}
                                            >
                                                <StyledMenuSectionTitle>Status </StyledMenuSectionTitle>
                                            </Grid>
                                            <Grid container>
                                                <Grid
                                                    item
                                                    xs={6}
                                                    sx={{
                                                        flexGrow: 0,
                                                        flexBasis: "50%",
                                                        maxWidth: "50%",
                                                        flexDirection: "none",
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="existing"
                                                        control={
                                                            <Checkbox
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "statusCriteria.existing",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    updateFilter(
                                                                        "statusCriteria.existing",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Existing"
                                                        labelPlacement="end"
                                                    />
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={6}
                                                    sx={{ flexGrow: 0, flexBasis: "50%", maxWidth: "50%" }}
                                                >
                                                    <FormControlLabel
                                                        value="proposed"
                                                        control={
                                                            <Checkbox
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "statusCriteria.proposed",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    updateFilter(
                                                                        "statusCriteria.proposed",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Proposed"
                                                        labelPlacement="end"
                                                    />
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={6}
                                                    sx={{ flexGrow: 0, flexBasis: "50%", maxWidth: "50%" }}
                                                >
                                                    <FormControlLabel
                                                        value="underConstruction"
                                                        control={
                                                            <Checkbox
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "statusCriteria.underConstruction",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    updateFilter(
                                                                        "statusCriteria.underConstruction",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Under Construction"
                                                        labelPlacement="end"
                                                    />
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={6}
                                                    sx={{ flexGrow: 0, flexBasis: "50%", maxWidth: "50%" }}
                                                >
                                                    <FormControlLabel
                                                        value="underRenovation"
                                                        control={
                                                            <Checkbox
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "statusCriteria.underRenovation",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    updateFilter(
                                                                        "statusCriteria.underRenovation",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Under Renovation"
                                                        labelPlacement="end"
                                                    />
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={6}
                                                    sx={{ flexGrow: 0, flexBasis: "50%", maxWidth: "50%" }}
                                                >
                                                    <FormControlLabel
                                                        value="demolished"
                                                        control={
                                                            <Checkbox
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "statusCriteria.demolished",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    updateFilter(
                                                                        "statusCriteria.demolished",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Demolished"
                                                        labelPlacement="end"
                                                    />
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={6}
                                                    sx={{ flexGrow: 0, flexBasis: "50%", maxWidth: "50%" }}
                                                >
                                                    <FormControlLabel
                                                        value="land"
                                                        control={
                                                            <Checkbox
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "statusCriteria.land",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    updateFilter(
                                                                        "statusCriteria.land",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Land"
                                                        labelPlacement="end"
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid container direction="row">
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sx={{ flexGrow: 0, flexBasis: "100%", maxWidth: "100%" }}
                                                >
                                                    <StyledMenuSectionTitle>Tenancy </StyledMenuSectionTitle>
                                                </Grid>
                                                <Grid item>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "tenancyCriteria.single",
                                                                    false
                                                                )}
                                                                onChange={(e) =>
                                                                    updateFilter(
                                                                        "tenancyCriteria.single",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Single"
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "tenancyCriteria.multi",
                                                                    false
                                                                )}
                                                                onChange={(e) =>
                                                                    updateFilter(
                                                                        "tenancyCriteria.multi",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Multi"
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid container justifyContent="flex-end">
                                                <Grid item>
                                                    <StyledMenuSearchButton
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </StyledMenuSearchButton>
                                                </Grid>
                                            </Grid>
                                        </StyledStatusContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <StyledFakeSelect onClick={(e) => handleClick("bottom-start")(e, "status")}>
                        {`Status ${
                            combineFilterCount(searchCriteria, "statusCriteria", "tenancyCriteria") > 0
                                ? `(${combineFilterCount(searchCriteria, "statusCriteria", "tenancyCriteria")})`
                                : ""
                        }`}
                        <StyledFakeSelectIcon>
                            <DownChevron />
                        </StyledFakeSelectIcon>
                    </StyledFakeSelect>
                </Grid>
                <Grid item xs sx={{ flexGrow: 1, flexBasis: 0, maxWidth: "100%", minWidth: "Auto" }}>
                    <Popper open={openDropdown === "size"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StyledStatusContainer>
                                            <Grid container justifyContent="space-between" alignItems="center">
                                                <Grid item>
                                                    <StyledSizeLabel
                                                        value="availableSf"
                                                        control={
                                                            <Radio
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "sizeCriteria.availableSf",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    handleSizeRadioClick(
                                                                        "availableSf",
                                                                        !get(
                                                                            searchCriteria,
                                                                            "sizeCriteria.availableSf",
                                                                            false
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Available SF"
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <FormControlLabel
                                                        value="totalSf"
                                                        control={
                                                            <Radio
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "sizeCriteria.totalSf",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    handleSizeRadioClick(
                                                                        "totalSf",
                                                                        !get(
                                                                            searchCriteria,
                                                                            "sizeCriteria.totalSf",
                                                                            false
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Total SF"
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <FormControlLabel
                                                        value="acreage"
                                                        control={
                                                            <Radio
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "sizeCriteria.acreage",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    handleSizeRadioClick(
                                                                        "acreage",
                                                                        !get(
                                                                            searchCriteria,
                                                                            "sizeCriteria.acreage",
                                                                            false
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Total Acreage"
                                                    />
                                                </Grid>
                                            </Grid>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: "1vw",
                                                }}
                                                justifyContent="space-between"
                                                spacing={4}
                                            >
                                                <BSInput
                                                    placeholder="Min SF"
                                                    value={get(searchCriteria, "sizeCriteria.min", "")}
                                                    onChange={(e) => updateFilter("sizeCriteria.min", e.target.value)}
                                                    onKeyPress={onKeyPress}
                                                    numeric
                                                />
                                                <div>&mdash;</div>
                                                <BSInput
                                                    placeholder="Max SF"
                                                    value={get(searchCriteria, "sizeCriteria.max", "")}
                                                    onChange={(e) => updateFilter("sizeCriteria.max", e.target.value)}
                                                    onKeyPress={onKeyPress}
                                                    numeric
                                                />
                                            </div>

                                            <Grid container justifyContent="flex-end" sx={{ paddingBottom: "6px" }}>
                                                <Grid item>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </StyledStatusContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <StyledFakeSelect onClick={(e) => handleClick("bottom-start")(e, "size")}>
                        {" "}
                        Size
                        <StyledFakeSelectIcon>
                            <DownChevron />
                        </StyledFakeSelectIcon>
                    </StyledFakeSelect>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        style={{ border: "1px solid rgba(0, 0, 0, 0.23)", color: "rgba(0, 0, 0, 0.87)" }}
                    >
                        Clear
                    </Button>
                </Grid>
            </Grid>
            <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                spacing={8}
                style={{ marginTop: "10px" }}
            >
                <Grid item>
                    {loadedSearchCrumbs
                        ? Object.keys(loadedSearchCrumbs).map((criteria) => {
                              if (loadedSearchCrumbs[criteria].value === true) {
                                  if (Object.keys(loadedSearchCrumbs[criteria]).length === 2) {
                                      return (
                                          <Chip
                                              style={{ marginLeft: "10px", marginBottom: "10px" }}
                                              color="primary"
                                              label={criteria}
                                              onDelete={() => handleDeleteCrumb(loadedSearchCrumbs[criteria], false)}
                                          />
                                      );
                                  } else if (
                                      (loadedSearchCrumbs[criteria].valueMin !== "* " &&
                                          loadedSearchCrumbs[criteria].valueMin !== undefined) ||
                                      (loadedSearchCrumbs[criteria].valueMax !== " *" &&
                                          loadedSearchCrumbs[criteria].valueMax !== undefined)
                                  ) {
                                      return (
                                          <Chip
                                              style={{ marginLeft: "10px", marginBottom: "10px" }}
                                              color="primary"
                                              label={
                                                  criteria +
                                                  get(loadedSearchCrumbs[criteria], "unit", "") +
                                                  ": " +
                                                  get(loadedSearchCrumbs[criteria], "valueMin", "* ") +
                                                  "-" +
                                                  get(loadedSearchCrumbs[criteria], "valueMax", " *")
                                              }
                                              onDelete={() => handleDeleteCrumb(loadedSearchCrumbs[criteria], null)}
                                          />
                                      );
                                  }
                              } else if (
                                  typeof loadedSearchCrumbs[criteria].value === "string" &&
                                  loadedSearchCrumbs[criteria].value != null
                              ) {
                                  return (
                                      <Chip
                                          style={{ marginLeft: "10px", marginBottom: "10px" }}
                                          color="primary"
                                          label={criteria + ": " + loadedSearchCrumbs[criteria].value}
                                          onDelete={() => handleDeleteCrumb(loadedSearchCrumbs[criteria], null)}
                                      />
                                  );
                              }
                              return null;
                          })
                        : ""}
                </Grid>
            </Grid>
        </StyledFilterBar>
    );
};

export default PropertiesFilters;
